import { Component, HostBinding, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { routePathName, routeUrl } from '@app/app.models';

import { finalize } from 'rxjs/operators';

import { environment } from '@env/environment';
import { config } from '@env/config';
import { I18nService } from '@app/i18n/services';
import { slideInOutAnimation } from '@shared/animations';

import { Endpoint, EndpointService } from '@services/endpoints';
import { AuthService } from '@services/auth';
import { FeedbackService } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';

import { FormEditorInputType } from '@app/shared/components';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	animations: [slideInOutAnimation]
})
export class LoginComponent implements OnInit {
	@HostBinding('@slideInOutAnimation')
	public formInputs = new FormGroup({
		email: new FormControl(this.authService.rememberMe, [Validators.required, Validators.email]),
		password: new FormControl('', [Validators.required]),
		rememberMe: new FormControl(this.authService.rememberMe)
	});

	public hidePassword = true;
	public disablePasswordAuth = false;
	public formEditorInputType = FormEditorInputType;
	private returnUrl: string;

	constructor(
		private authService: AuthService,
		private feedbackService: FeedbackService,
		private progressIndicatorService: ProgressIndicatorService,
		private i18nService: I18nService,
		private route: ActivatedRoute,
		private endpointService: EndpointService
	) {}

	public ngOnInit(): void {
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
		this.authService.returnUrl = this.returnUrl;

		this.progressIndicatorService.spinner(false);
		this.onEmailBlur();

		this.formInputs.get('rememberMe').valueChanges.subscribe((value: boolean) => {
			this.authService.rememberMe =
				value && this.formInputs.controls.email.valid && this.formInputs.controls.email.value
					? this.formInputs.controls.email.value
					: '';
		});
	}

	public onEmailBlur(): void {
		const emailValue = (this.formInputs.controls.email.value || '').toLowerCase();
		this.disablePasswordAuth = emailValue.endsWith('@specialized.com');

		if (this.disablePasswordAuth) {
			this.formInputs.controls.password.reset('');
			this.formInputs.controls.password.disable({ emitEvent: false });
		} else {
			this.formInputs.controls.password.enable({ emitEvent: false });
		}
	}

	public togglePassHide() {
		this.hidePassword = !this.hidePassword;
	}

	public onFormSubmit() {
		if (this.disablePasswordAuth) {
			return;
		}

		if (this.formInputs.invalid) {
			this.formInputs.controls.email.markAsDirty();
			this.formInputs.controls.password.markAsDirty();
			this.formInputs.updateValueAndValidity();
			return;
		}

		this.progressIndicatorService.spinner(true);
		this.authService.rememberMe = this.formInputs.get('email').value;

		this.authService
			.login(this.formInputs.get('email').value, this.formInputs.get('password').value)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(result: any) => {

					if (this.authService.isAuthenticated) {
						this.navigate(this.returnUrl ? this.returnUrl : environment.apps.portal);
					} else {
						this.feedbackService.apiError(
							{},
							this.i18nService.get('Try again', 'app.api.response.incorrect-username-password')
						);
					}
				},
				(error: any) => this.feedbackService.apiError(error)
			);
	}

	public employeeSSO(): void {
		this.navigate(this.endpointService.url(Endpoint.employeeSSO, { client_id: config.entraClientId, state: "adminportal", redirect_uri: `${environment.apps.portal}${routeUrl(routePathName.oauthCallback)}` }));
	}

	private navigate(url: string): void {
		window.location.assign(url);
	}
}

